import React from 'react'
import { forwardRefType } from '../utils/forward-ref-type'
import { composeRefs } from '../utils/compose-refs'
import { SlideContext } from './slide'
import { useCarouselContext } from './carousel-context'
import { SlideContainerContext } from './slide-container'

export interface SlideViewportProps<T> {
  renderSlide(prop: { value: T; index: number }): React.ReactNode
}

function SlideViewportRaw<T>({ renderSlide }: SlideViewportProps<T>, ref: React.Ref<HTMLDivElement>) {
  const { slideViewpointRef, wrapAround, slideCount, slidesToShow, slides } = useCarouselContext()
  const { positioning } = React.useContext(SlideContainerContext)
  // When wrapAround is enabled, we show the slides 3 times
  const renderedSlideCount = wrapAround ? 3 * slideCount : slideCount

  const listVisibleWidth = `${(renderedSlideCount * 100) / slidesToShow}%`

  const renderSlides = React.useCallback(
    (typeOfSlide?: 'prev-cloned' | 'next-cloned') => {
      return slides.map((child, index) => {
        return (
          <SlideContext.Provider
            value={{
              index: index,
              typeOfSlide
            }}
            key={`${typeOfSlide}-${index}`}
          >
            {renderSlide({ value: child, index })}
          </SlideContext.Provider>
        )
      })
    },
    [renderSlide, slides]
  )

  return (
    <div
      ref={composeRefs(ref, slideViewpointRef)}
      style={{
        width: listVisibleWidth,
        textAlign: 'left',
        userSelect: 'auto',
        transform: positioning,
        display: 'flex'
      }}
    >
      {wrapAround ? renderSlides('prev-cloned') : null}
      {renderSlides()}
      {wrapAround ? renderSlides('next-cloned') : null}
    </div>
  )
}
export const SlideViewport = (React.forwardRef as forwardRefType)(SlideViewportRaw)
